import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import Layout from "@/components/Layout";
import { http } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import { Send, MessageSquare, Building2, Loader2, Paperclip, X, Check, CheckCheck, Wifi, WifiOff, FileText, Download, Search } from "lucide-react";
import { toast } from "sonner";
import { subscribeWS, sendWS, wsReady } from "@/lib/ws";

const MAX_ATTACHMENT_BYTES = 900 * 1024; // ~900 KB raw before base64 blow-up

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const url = reader.result || "";
      const b64 = String(url).split(",")[1] || "";
      resolve({ b64, dataUrl: String(url) });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function Messages() {
  const { user, lang, loading } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [threads, setThreads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const [attachment, setAttachment] = useState(null); // { b64, dataUrl, name, type }
  const [peerTyping, setPeerTyping] = useState(false);
  const [connected, setConnected] = useState(false);
  const [search, setSearch] = useState("");
  const scrollerRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const lastTypingSentRef = useRef(0);
  const selectedRef = useRef(null);
  useEffect(() => { selectedRef.current = selected; }, [selected]);

  const loadThreads = useCallback(async () => {
    try {
      const { data } = await http.get("/messages/threads");
      setThreads(data.threads || []);
      return data.threads || [];
    } catch { return []; }
    // http/setThreads are stable references; adding them would add churn without
    // changing behaviour — the hook only needs to re-create on mount.
  }, []);

  const loadThread = useCallback(async (peer_id, company_id) => {
    setLoadingThread(true);
    try {
      const q = company_id ? `?company_id=${encodeURIComponent(company_id)}` : "";
      const { data } = await http.get(`/messages/thread/${peer_id}${q}`);
      setMessages(data.messages || []);
      setSelected({
        peer_id,
        peer_name: data.peer?.name || "Utilisateur",
        peer_picture: data.peer?.picture,
        company_id: company_id || null,
        company_name: data.company?.name || null,
      });
      setPeerTyping(false);
      setTimeout(() => scrollerRef.current?.scrollTo({ top: 1e9, behavior: "smooth" }), 60);
    } finally {
      setLoadingThread(false);
    }
    // http, setters, refs are stable — see loadThreads note above.
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    loadThreads().then((list) => {
      const to = params.get("to");
      const company = params.get("company");
      if (to) loadThread(to, company || undefined);
      else if (list.length > 0) loadThread(list[0].peer_id, list[0].company_id || undefined);
    });
    // Runs once when auth resolves — re-firing on every loadThreads/loadThread/params
    // change would race with the WS subscription below. Both loaders + nav
    // are stable via useCallback, so this is safe to gate on [user, loading].
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading]);

  // WebSocket subscription for real-time events
  useEffect(() => {
    if (!user) return;
    const unsub = subscribeWS((evt) => {
      if (evt.type === "__open__") { setConnected(true); return; }
      if (evt.type === "pong") return;
      if (evt.type === "message") {
        const m = evt.data;
        const active = selectedRef.current;
        const activeCid = active?.company_id || null;
        const evtCid = m.company_id || null;
        if (active && m.from_user_id === active.peer_id && evtCid === activeCid) {
          setMessages((prev) => [...prev, m]);
          setPeerTyping(false);
          setTimeout(() => scrollerRef.current?.scrollTo({ top: 1e9, behavior: "smooth" }), 40);
          // I'm actively viewing → mark read on server (calling thread endpoint refreshes read_at)
          const q = activeCid ? `?company_id=${encodeURIComponent(activeCid)}` : "";
          http.get(`/messages/thread/${active.peer_id}${q}`).catch(() => {});
        }
        loadThreads();
      } else if (evt.type === "typing") {
        const active = selectedRef.current;
        const activeCid = active?.company_id || null;
        const evtCid = evt.company_id || null;
        if (active && evt.from_user_id === active.peer_id && evtCid === activeCid) {
          setPeerTyping(true);
          if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = setTimeout(() => setPeerTyping(false), 3500);
        }
      } else if (evt.type === "read") {
        const active = selectedRef.current;
        if (active && evt.reader_id === active.peer_id) {
          setMessages((prev) => prev.map((m) => m.to_user_id === evt.reader_id && !m.read ? { ...m, read: true, read_at: evt.read_at } : m));
        }
      }
    });
    return () => { setConnected(false); unsub(); };
    // eslint-disable-next-line
  }, [user]);

  const emitTyping = () => {
    if (!selected) return;
    const now = Date.now();
    if (now - lastTypingSentRef.current < 1500) return;
    lastTypingSentRef.current = now;
    sendWS({ type: "typing", to_user_id: selected.peer_id, company_id: selected.company_id });
  };

  const pickAttachment = async (file) => {
    if (!file) return;
    const isImage = file.type.startsWith("image/");
    const isPdf = file.type === "application/pdf";
    if (!isImage && !isPdf) { toast.error(lang === "fr" ? "Images et PDF uniquement" : "Images and PDFs only"); return; }
    if (file.size > MAX_ATTACHMENT_BYTES) { toast.error(lang === "fr" ? "Fichier trop lourd (max ~900 Ko)" : "File too large (max ~900 KB)"); return; }
    const { b64, dataUrl } = await fileToBase64(file);
    setAttachment({ b64, dataUrl, name: file.name, type: file.type, size: file.size });
  };

  const send = async () => {
    if (!selected) return;
    if (!body.trim() && !attachment) return;
    setSending(true);
    try {
      await http.post("/messages/send", {
        to_user_id: selected.peer_id,
        company_id: selected.company_id,
        body: body.trim(),
        attachment_b64: attachment?.b64,
        attachment_type: attachment?.type,
        attachment_name: attachment?.name,
      });
      setBody("");
      setAttachment(null);
      await loadThread(selected.peer_id, selected.company_id || undefined);
      loadThreads();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (lang === "fr" ? "Erreur d'envoi" : "Send failed"));
    } finally {
      setSending(false);
    }
  };

  const activeKey = useMemo(() => selected ? `${selected.peer_id}::${selected.company_id || ""}` : "", [selected]);

  const filteredThreads = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return threads;
    return threads.filter((tr) =>
      (tr.peer_name || "").toLowerCase().includes(q) ||
      (tr.company_name || "").toLowerCase().includes(q) ||
      (tr.last_message || "").toLowerCase().includes(q)
    );
  }, [threads, search]);

  const lastMineIndex = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].from_user_id === user?.user_id) return i;
    }
    return -1;
  }, [messages, user]);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center gap-2 mb-6">
          <MessageSquare className="w-5 h-5 text-[#F97316]" />
          <h1 data-testid="messages-title" className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#1E3A8A]">
            {lang === "fr" ? "Messagerie" : "Messages"}
          </h1>
          <span data-testid="ws-status" className={`ml-3 text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1 ${connected ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
            {connected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
            {connected ? (lang === "fr" ? "Temps réel" : "Live") : (lang === "fr" ? "Reconnexion…" : "Reconnecting…")}
          </span>
        </div>

        <div className="grid md:grid-cols-[320px_1fr] gap-4 rounded-3xl bg-white border border-slate-200 overflow-hidden min-h-[560px]">
          <aside data-testid="messages-threads" className="border-r border-slate-200 max-h-[70vh] overflow-y-auto flex flex-col">
            <div className="p-2 border-b border-slate-100 sticky top-0 bg-white z-10">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <input
                  data-testid="messages-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={lang === "fr" ? "Rechercher (nom, entreprise)…" : "Search (name, company)…"}
                  className="w-full pl-8 pr-3 py-2 text-xs rounded-full border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#1E3A8A] outline-none transition-colors"
                />
                {search && (
                  <button
                    data-testid="messages-search-clear"
                    onClick={() => setSearch("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full text-slate-400 hover:text-slate-700 flex items-center justify-center"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
            {threads.length === 0 && (
              <div className="p-8 text-center text-sm text-slate-500">
                {lang === "fr"
                  ? "Aucune conversation. Contactez une entreprise depuis sa fiche vitrine pour démarrer un échange."
                  : "No conversations yet. Contact a company from its showcase to start one."}
              </div>
            )}
            {threads.length > 0 && filteredThreads.length === 0 && (
              <div data-testid="messages-search-empty" className="p-6 text-center text-xs text-slate-400">
                {lang === "fr" ? "Aucun résultat pour cette recherche." : "No results for this search."}
              </div>
            )}
            {filteredThreads.map((tr) => {
              const key = `${tr.peer_id}::${tr.company_id || ""}`;
              const active = key === activeKey;
              return (
                <button
                  key={tr.thread_id}
                  data-testid={`thread-${tr.peer_id}`}
                  onClick={() => { setParams({ to: tr.peer_id, ...(tr.company_id ? { company: tr.company_id } : {}) }); loadThread(tr.peer_id, tr.company_id || undefined); }}
                  className={`w-full text-left p-3 border-b border-slate-100 transition-colors ${active ? "bg-[#F97316]/10" : "hover:bg-slate-50"}`}
                >
                  <div className="flex items-center gap-3">
                    <Avatar className="w-10 h-10 shrink-0">
                      <AvatarImage src={tr.peer_picture} />
                      <AvatarFallback className="bg-[#1E3A8A] text-white text-xs">{(tr.peer_name || "?")[0]?.toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="font-medium text-sm truncate">{tr.peer_name}</p>
                        {tr.unread > 0 && (
                          <span className="ml-auto shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-[#F97316] text-white text-[10px] font-bold flex items-center justify-center">
                            {tr.unread}
                          </span>
                        )}
                      </div>
                      {tr.company_name && (
                        <p className="text-[11px] text-slate-500 truncate flex items-center gap-1">
                          <Building2 className="w-3 h-3" /> {tr.company_name}
                        </p>
                      )}
                      <p className={`text-xs truncate mt-0.5 ${tr.unread > 0 ? "text-slate-800 font-medium" : "text-slate-500"}`}>
                        {tr.last_from_me ? (lang === "fr" ? "Vous : " : "You: ") : ""}{tr.last_message}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </aside>

          <section className="flex flex-col min-h-[560px]">
            {!selected && (
              <div className="flex-1 flex items-center justify-center text-slate-400 text-sm p-8 text-center">
                {lang === "fr" ? "Sélectionnez une conversation pour commencer." : "Select a conversation to start."}
              </div>
            )}

            {selected && (
              <>
                <header className="p-4 border-b border-slate-200 flex items-center gap-3">
                  <Avatar className="w-10 h-10">
                    <AvatarImage src={selected.peer_picture} />
                    <AvatarFallback className="bg-[#1E3A8A] text-white text-xs">{selected.peer_name?.[0]?.toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{selected.peer_name}</p>
                    {selected.company_name && (
                      <p className="text-[11px] text-slate-500 truncate flex items-center gap-1">
                        <Building2 className="w-3 h-3" /> {selected.company_name}
                      </p>
                    )}
                  </div>
                </header>

                <div data-testid="messages-scroller" ref={scrollerRef} className="flex-1 overflow-y-auto p-4 space-y-2 bg-[#F8FAFC] max-h-[55vh]">
                  {loadingThread && <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div>}
                  {!loadingThread && messages.length === 0 && (
                    <p className="text-center text-slate-400 text-sm py-8">
                      {lang === "fr" ? "Écrivez le premier message." : "Send the first message."}
                    </p>
                  )}
                  {messages.map((m, idx) => {
                    const mine = m.from_user_id === user.user_id;
                    const dataUrl = m.attachment_b64 && m.attachment_type ? `data:${m.attachment_type};base64,${m.attachment_b64}` : null;
                    const isImage = (m.attachment_type || "").startsWith("image/");
                    const isPdf = m.attachment_type === "application/pdf";
                    const showReadReceipt = mine && idx === lastMineIndex;
                    return (
                      <div key={m.id} data-testid={`msg-${m.id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${mine ? "bg-[#1E3A8A] text-white rounded-br-md" : "bg-white border border-slate-200 rounded-bl-md"}`}>
                          {dataUrl && isImage && (
                            <a href={dataUrl} download={m.attachment_name || "image"} className="block mb-1.5">
                              <img data-testid={`msg-img-${m.id}`} src={dataUrl} alt={m.attachment_name || "image"} className="max-w-full max-h-64 rounded-xl border border-white/10" />
                            </a>
                          )}
                          {dataUrl && isPdf && (
                            <a
                              data-testid={`msg-pdf-${m.id}`}
                              href={dataUrl}
                              download={m.attachment_name || "document.pdf"}
                              className={`mb-1.5 flex items-center gap-2.5 rounded-xl px-3 py-2.5 border ${mine ? "bg-white/10 border-white/20 hover:bg-white/20" : "bg-slate-50 border-slate-200 hover:bg-slate-100"} transition-colors`}
                            >
                              <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${mine ? "bg-white/20" : "bg-[#F97316]/10"}`}>
                                <FileText className={`w-4 h-4 ${mine ? "text-white" : "text-[#F97316]"}`} />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className={`text-xs font-semibold truncate ${mine ? "text-white" : "text-slate-800"}`}>{m.attachment_name || "document.pdf"}</p>
                                <p className={`text-[10px] ${mine ? "text-white/60" : "text-slate-500"}`}>PDF</p>
                              </div>
                              <Download className={`w-3.5 h-3.5 shrink-0 ${mine ? "text-white/70" : "text-slate-400"}`} />
                            </a>
                          )}
                          {m.body && <p className="whitespace-pre-wrap leading-relaxed">{m.body}</p>}
                          <div className={`flex items-center gap-1.5 justify-end mt-1 text-[10px] ${mine ? "text-white/70" : "text-slate-400"}`}>
                            <span>{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                            {showReadReceipt && (
                              m.read
                                ? <span data-testid={`msg-read-${m.id}`} title={lang === "fr" ? "Lu" : "Read"} className="text-emerald-300"><CheckCheck className="w-3.5 h-3.5" /></span>
                                : <span data-testid={`msg-sent-${m.id}`} title={lang === "fr" ? "Envoyé" : "Sent"}><Check className="w-3.5 h-3.5" /></span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {peerTyping && (
                    <div data-testid="peer-typing" className="flex justify-start">
                      <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-md px-4 py-2.5 text-xs text-slate-500 italic flex items-center gap-1.5">
                        <span className="inline-flex gap-0.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "120ms" }} />
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "240ms" }} />
                        </span>
                        {lang === "fr" ? "en train d'écrire…" : "typing…"}
                      </div>
                    </div>
                  )}
                </div>

                <footer className="p-3 border-t border-slate-200 bg-white">
                  {attachment && (
                    <div data-testid="attachment-preview" className="mb-2 relative inline-flex items-center gap-2 rounded-xl overflow-hidden border border-slate-200 bg-slate-50 pr-8 pl-2 py-1.5">
                      {attachment.type.startsWith("image/") ? (
                        <img src={attachment.dataUrl} alt={attachment.name} className="h-16 w-auto rounded-lg" />
                      ) : (
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 rounded-lg bg-[#F97316]/10 flex items-center justify-center">
                            <FileText className="w-5 h-5 text-[#F97316]" />
                          </div>
                          <div className="min-w-0 max-w-[240px]">
                            <p className="text-xs font-semibold truncate">{attachment.name}</p>
                            <p className="text-[10px] text-slate-500">PDF · {Math.round((attachment.size || 0) / 1024)} Ko</p>
                          </div>
                        </div>
                      )}
                      <button
                        data-testid="attachment-remove"
                        onClick={() => setAttachment(null)}
                        className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                  <div className="flex items-end gap-2">
                    <label data-testid="attachment-btn" className="cursor-pointer shrink-0 rounded-full p-2 border border-slate-200 hover:bg-slate-50 transition-colors" title={lang === "fr" ? "Joindre une image ou un PDF" : "Attach image or PDF"}>
                      <Paperclip className="w-4 h-4 text-slate-600" />
                      <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => pickAttachment(e.target.files?.[0])} />
                    </label>
                    <Textarea
                      data-testid="message-input"
                      value={body}
                      onChange={(e) => { setBody(e.target.value); emitTyping(); }}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                      placeholder={lang === "fr" ? "Écrire un message… (Entrée pour envoyer)" : "Type a message… (Enter to send)"}
                      rows={2}
                      className="resize-none rounded-2xl border-slate-200"
                    />
                    <Button
                      data-testid="message-send"
                      onClick={send}
                      disabled={sending || (!body.trim() && !attachment)}
                      className="bg-[#F97316] hover:bg-[#EA580C] text-white rounded-full h-11 px-5 shrink-0"
                    >
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                </footer>
              </>
            )}
          </section>
        </div>
      </div>
    </Layout>
  );
}
